import type { DriveStep, Side } from "driver.js";
import { clickToContinue, el } from "../tour/stepHelpers";
import type { StepData, TourChapter, TourContext } from "../tour/types";
import type { TranslationKey } from "../i18n";
import { demoStore } from "./demoStore";

const S = () => demoStore.getState();
const hasProject = () => !!S().project;
const hasVerified = () => S().target?.verification_status === "verified";
const hasScan = () => !!S().scan;
const cp = () => S().scan?.checkpoint ?? -1;
const completed = () => S().scan?.status === "completed";

type T = TourContext["t"];

/** Advance the scan up to checkpoint `k` — loops so a skipped intermediate
 *  step can't leave the scan stuck below the target, and re-entering a step
 *  (Back then Forward) never over-advances. */
const advanceTo = (k: number) => () => {
  let guard = 0;
  while (cp() < k && guard++ < 6) demoStore.advanceScan();
};

/** A view step on the scan. Entering it advances the scan to `toCheckpoint`
 *  (when given), so the stage the step describes is on screen; moving on is a
 *  plain Next. Advancing on entry (not on Next) keeps the checkpoint in lockstep
 *  with the steps and avoids the live-step unmount races. */
function viewStep(t: T, anchor: string, key: string, side: Side, toCheckpoint?: number): DriveStep {
  return {
    element: el(anchor),
    popover: {
      title: t(`${key}.title` as TranslationKey),
      description: t(`${key}.body` as TranslationKey),
      side,
      align: "start",
    },
    onHighlightStarted: toCheckpoint !== undefined ? advanceTo(toCheckpoint) : undefined,
  };
}

/** The demo, chapter by chapter (index = chapter number; 0 is the welcome
 *  card, shown before the demo is on). Steps read the demo store to know when
 *  to move on. Copy lives in i18n under `tour.*`. */
export const DEMO_CHAPTERS: TourChapter[] = [
  // 0 — welcome card (before the demo is on).
  {
    route: ["/overview", "/onboarding"],
    waitFor: el("sidebar-overview"),
    delayMs: 400,
    steps: ({ t, userName }) => [
      {
        popover: {
          title: userName ? t("tour.welcome.titlenamed").replace("{name}", userName) : t("tour.welcome.title"),
          description: t("tour.welcome.body"),
          showButtons: ["next"],
          nextBtnText: t("tour.welcome.start"),
          popoverClass: "ces-tour ces-tour-welcome",
        },
        data: { secondary: { label: t("tour.skip"), action: "skip" }, startsDemo: true } satisfies StepData,
      },
    ],
  },
  // 1 — add a site (onboarding form, shown at the overview's empty state while
  // the demo has no project). A separate chapter from DNS so that clicking
  // Register — which unmounts this form — advances to the DNS chapter by its
  // state guard instead of restarting this one.
  {
    route: "/overview",
    when: () => !hasProject(),
    waitFor: el("site-host"),
    steps: ({ t }) => [
      {
        element: el("site-host"),
        popover: { title: t("tour.site.title"), description: t("tour.site.body"), side: "bottom", align: "start" },
      },
      {
        element: el("register-site"),
        popover: { title: t("tour.register.title"), description: t("tour.register.body"), side: "bottom", align: "start" },
        data: {
          nextHidden: true,
          live: {
            observe: "document",
            advanceWhen: () => hasProject(),
            nudge: { text: t("tour.register.nudge") },
          },
        } satisfies StepData,
      },
    ],
  },
  // 2 — verify the site's DNS.
  {
    route: "/overview",
    when: () => hasProject() && !hasVerified(),
    waitFor: el("dns-card"),
    steps: ({ t }) => [
      {
        element: el("dns-card"),
        popover: { title: t("tour.dns.title"), description: t("tour.dns.body"), side: "top", align: "start" },
        data: { reveal: "center" } satisfies StepData,
      },
      {
        element: el("verify-site"),
        popover: { title: t("tour.verify.title"), description: t("tour.verify.body"), side: "top", align: "start" },
        data: {
          nextHidden: true,
          live: {
            observe: "document",
            advanceWhen: () => hasVerified(),
            nudge: { text: t("tour.verify.nudge") },
          },
        } satisfies StepData,
      },
    ],
  },
  // 3 — start the scan.
  {
    route: "/overview",
    when: () => hasVerified() && !hasScan(),
    waitFor: el("scan-cta"),
    steps: ({ t }) => [
      {
        element: el("project-pill"),
        popover: { title: t("tour.project.title"), description: t("tour.project.body"), side: "bottom", align: "start" },
      },
      {
        element: el("scan-cta"),
        popover: { title: t("tour.start.title"), description: t("tour.start.body"), side: "top", align: "start" },
        data: {
          nextHidden: true,
          live: {
            observe: "document",
            advanceWhen: () => hasScan(),
            nudge: { text: t("tour.start.nudge") },
          },
        } satisfies StepData,
      },
    ],
  },
  // 4 — the scan runs: recon → map → analyze → exploit, ending by opening the
  // critical finding.
  {
    route: "/overview",
    when: () => hasScan() && !completed(),
    waitFor: el("scan-stages"),
    steps: ({ t }) => [
      // RECON is where the scan already is; the next views advance it one stage
      // each as they're entered (MAP, then ANALYZE, then EXPLOIT).
      viewStep(t, "scan-stages", "tour.recon", "bottom"),
      viewStep(t, "overview-graph", "tour.map", "top", 1),
      {
        element: el("chat-rail"),
        popover: { title: t("tour.chat.title"), description: t("tour.chat.body"), side: "left", align: "start" },
        onHighlightStarted: advanceTo(2), // → ANALYZE
        data: {
          nextHidden: true,
          live: {
            observe: "document",
            advanceWhen: () => !!document.querySelector(".chat--open"),
            nudge: { text: t("tour.chat.nudge"), open: () => (document.querySelector(el("chat-rail")) as HTMLElement | null)?.click() },
          },
        } satisfies StepData,
      },
      // A plain step on the open panel so the user can read the narration before
      // moving on (opening the chat used to jump straight ahead).
      {
        element: el("chat-panel"),
        popover: { title: t("tour.chatRead.title"), description: t("tour.chatRead.body"), side: "left", align: "start" },
      },
      // Entering the metrics step pushes the scan to EXPLOIT: the first finding
      // lands and the three counters tick up from their placeholders, so the
      // user sees the threat count grow.
      viewStep(t, "metrics", "tour.analyze", "bottom", 3),
      {
        // Point at the finding row itself (not the whole card), so it's clear
        // exactly what to click.
        element: el("finding-row"),
        popover: { title: t("tour.finding.title"), description: t("tour.finding.body"), side: "top", align: "start" },
        data: clickToContinue(t("tour.finding.nudge"), "document") satisfies StepData,
      },
    ],
  },
  // 5 — the finding on the Findings page: read it, how to fix it, then the report.
  {
    route: "/findings",
    when: () => hasScan(),
    waitFor: el("finding-card"),
    steps: ({ t }) => [
      {
        element: el("finding-card"),
        popover: { title: t("tour.read.title"), description: t("tour.read.body"), side: "bottom", align: "start" },
        data: {
          nextHidden: true,
          live: {
            observe: "document",
            advanceWhen: () => !!document.querySelector(".finding-card--expanded"),
            nudge: { text: t("tour.read.nudge"), open: () => (document.querySelector(`${el("finding-card")} .finding-card__head`) as HTMLElement | null)?.click() },
          },
        } satisfies StepData,
      },
      {
        element: el("finding-fix"),
        popover: { title: t("tour.fix.title"), description: t("tour.fix.body"), side: "top", align: "start" },
        onHighlightStarted: advanceTo(4), // → CHAIN: more findings arrive below
        data: { reveal: "center" } satisfies StepData,
      },
      {
        element: el("sidebar-reports"),
        popover: { title: t("tour.toReport.title"), description: t("tour.toReport.body"), side: "right", align: "start" },
        data: clickToContinue(t("tour.toReport.nudge")) satisfies StepData,
      },
    ],
  },
  // 6 — the report: read it, download it, finish.
  {
    route: "/reports",
    when: () => hasScan(),
    waitFor: el("report-row"),
    steps: ({ t }) => [
      {
        element: el("report-row"),
        popover: { title: t("tour.report.title"), description: t("tour.report.body"), side: "bottom", align: "start" },
        onHighlightStarted: advanceTo(5), // complete the scan → the report is ready
      },
      // Download before Read: Read opens the preview modal, which would sit over
      // the Download button and break its highlight.
      {
        element: el("report-download"),
        popover: { title: t("tour.download.title"), description: t("tour.download.body"), side: "top", align: "end" },
      },
      {
        element: el("report-read"),
        popover: { title: t("tour.readReport.title"), description: t("tour.readReport.body"), side: "top", align: "end" },
        data: {
          nextHidden: true,
          live: {
            observe: "document",
            advanceWhen: () => !!document.querySelector(".reports__preview-frame"),
            nudge: { text: t("tour.readReport.nudge"), open: () => (document.querySelector(el("report-read")) as HTMLElement | null)?.click() },
          },
        } satisfies StepData,
      },
      {
        element: el("report-preview"),
        popover: { title: t("tour.preview.title"), description: t("tour.preview.body"), side: "left", align: "start" },
      },
      {
        popover: { title: t("tour.done.title"), description: t("tour.done.body"), nextBtnText: t("tour.done.cta"), showButtons: ["next"] },
        data: { finishDemo: true, navigateTo: "/overview" } satisfies StepData,
      },
    ],
  },
];
